import React, { useState, useRef, useEffect } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
} from '@backstage/core-components';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import PersonIcon from '@material-ui/icons/Person';
import InfoIcon from '@material-ui/icons/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 250px)',
    maxHeight: '800px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  message: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    alignItems: 'flex-start',
  },
  userMessage: {
    justifyContent: 'flex-end',
  },
  assistantMessage: {
    justifyContent: 'flex-start',
  },
  messageContent: {
    maxWidth: '70%',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
  },
  userMessageContent: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantMessageContent: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  messageIcon: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    '& > *': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  apiResult: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  toolsPanel: {
    marginBottom: theme.spacing(2),
  },
  codeBlock: {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    overflowX: 'auto',
    marginTop: theme.spacing(1),
  },
}));

interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolResults?: any;
  timestamp: Date;
}

const EXAMPLE_QUERIES = [
  'Find APIs related to projects',
  'Search for user management APIs',
  'What APIs work with companies?',
  'List all platform APIs',
  'Show me the permissions API details',
  'Find APIs in the preconstruction domain',
];

export const ApiAgentPage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load available tools on mount
  useEffect(() => {
    const loadTools = async () => {
      try {
        const response = await fetchApi.fetch(`${backendUrl}/api/api-agent-mcp/tools`);
        const data = await response.json();
        setAvailableTools(data.tools || []);
      } catch (error) {
        console.error('Failed to load tools:', error);
      }
    };
    loadTools();
  }, [backendUrl, fetchApi]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Determine which tool to use based on the query
      const toolResult = await executeQuery(input);

      const assistantMessage: Message = {
        role: 'assistant',
        content: generateResponse(input, toolResult),
        toolResults: toolResult,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Error: ${error.message}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const executeQuery = async (query: string): Promise<any> => {
    const lowerQuery = query.toLowerCase();

    // Determine which tool to use
    let toolName = 'search_apis';
    let params: any = { query };

    if (lowerQuery.includes('list all') || lowerQuery.includes('show all')) {
      toolName = 'list_all_apis';
      params = {};

      // Extract domain if mentioned
      const domains = ['commercial', 'highways', 'operations', 'platform', 'preconstruction', 'reference', 'trading', 'workforce'];
      const matchedDomain = domains.find(d => lowerQuery.includes(d));
      if (matchedDomain) {
        params.domain = matchedDomain;
      }
    } else if (lowerQuery.includes('related to') || lowerQuery.includes('work with')) {
      toolName = 'find_related_apis';
      // Extract entity type - look for the word after "to" or "with"
      let entityType = '';

      if (lowerQuery.includes('related to')) {
        const match = query.match(/related to\s+(\w+)/i);
        if (match) {
          entityType = match[1].replace(/[?.,]/g, '');
        }
      } else if (lowerQuery.includes('work with')) {
        const match = query.match(/work with\s+(\w+)/i);
        if (match) {
          entityType = match[1].replace(/[?.,]/g, '');
        }
      }

      if (entityType) {
        params = { entityType };
      }
    } else if (lowerQuery.includes('details') || lowerQuery.includes('show me')) {
      // Extract API name
      const apiNameMatch = query.match(/(?:details|show me|about)\s+(?:the\s+)?([a-z-]+)\s*(?:api)?/i);
      if (apiNameMatch) {
        toolName = 'get_api_details';
        params = { apiName: apiNameMatch[1] };
      }
    }

    // Execute the tool
    const response = await fetchApi.fetch(`${backendUrl}/api/api-agent-mcp/tools/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toolName, parameters: params }),
    });

    if (!response.ok) {
      throw new Error('Failed to execute query');
    }

    const data = await response.json();
    return { toolName, ...data.result };
  };

  const generateResponse = (query: string, toolResult: any): string => {
    // Handle errors with suggestions
    if (toolResult.error && toolResult.suggestions) {
      return toolResult.error;
    }

    // Handle regular errors
    if (toolResult.error) {
      return `I couldn't find that information. ${toolResult.error}`;
    }

    const { toolName } = toolResult;

    if (toolName === 'search_apis') {
      const count = toolResult.resultsCount || 0;
      if (count === 0) {
        return `I didn't find any APIs matching "${query}". Try broader search terms or check the available domains.`;
      }
      return `I found ${count} API${count !== 1 ? 's' : ''} matching your query:`;
    }

    if (toolName === 'list_all_apis') {
      const count = toolResult.totalApis || 0;
      const domain = toolResult.filters?.domain || 'all domains';
      return `Here are ${count} APIs${domain !== 'all domains' ? ` in the ${domain} domain` : ''}:`;
    }

    if (toolName === 'find_related_apis') {
      const count = toolResult.resultsCount || 0;
      if (count === 0) {
        return `I didn't find any APIs related to "${toolResult.entityType}". Try a different entity type.`;
      }
      return `I found ${count} API${count !== 1 ? 's' : ''} that work with "${toolResult.entityType}":`;
    }

    if (toolName === 'get_api_details') {
      return `Here are the details for the ${toolResult.title || toolResult.name} API:`;
    }

    return 'Here are the results:';
  };

  const renderToolResults = (toolResult: any) => {
    if (!toolResult) return null;

    // Handle errors with suggestions (from get_api_details)
    if (toolResult.error && toolResult.suggestions) {
      return (
        <Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            {toolResult.error}
          </Typography>
          <List dense>
            {toolResult.suggestions.map((api: any, idx: number) => {
              const catalogUrl = `/catalog/default/api/${api.name}`;
              return (
                <React.Fragment key={idx}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: '8px' }}>
                          <Link href={catalogUrl} color="primary" style={{ textDecoration: 'none' }}>
                            <Typography variant="subtitle2" style={{ cursor: 'pointer' }}>
                              {api.title || api.name}
                            </Typography>
                          </Link>
                        </Box>
                      }
                      secondary={
                        api.tags && api.tags.length > 0 && (
                          <Box mt={0.5} display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
                            {api.tags.map((tag: string, tagIdx: number) => (
                              <Chip
                                key={tagIdx}
                                label={tag}
                                size="small"
                                variant="outlined"
                                style={{ height: '20px', fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        )
                      }
                    />
                  </ListItem>
                  {idx < toolResult.suggestions.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      );
    }

    // Handle regular errors
    if (toolResult.error) return null;

    const { toolName } = toolResult;

    if (toolName === 'search_apis' || toolName === 'find_related_apis') {
      return (
        <List dense>
          {toolResult.results?.map((api: any, idx: number) => {
            // Construct the catalog URL for this API
            const catalogUrl = `/catalog/default/api/${api.name}`;

            return (
              <React.Fragment key={idx}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: '8px' }}>
                        <Link href={catalogUrl} color="primary" style={{ textDecoration: 'none' }}>
                          <Typography variant="subtitle2" style={{ cursor: 'pointer' }}>
                            {api.title || api.name}
                          </Typography>
                        </Link>
                        {api.system && <Chip size="small" label={api.system} />}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2">{api.description}</Typography>
                        {api.tags && api.tags.length > 0 && (
                          <Box mt={0.5} display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
                            {api.tags.slice(0, 8).map((tag: string, tagIdx: number) => (
                              <Chip
                                key={tagIdx}
                                label={tag}
                                size="small"
                                variant="outlined"
                                style={{ height: '20px', fontSize: '0.7rem' }}
                              />
                            ))}
                            {api.tags.length > 8 && (
                              <Typography variant="caption" color="textSecondary" style={{ lineHeight: '20px' }}>
                                +{api.tags.length - 8} more
                              </Typography>
                            )}
                          </Box>
                        )}
                        {api.matchedEntities && (
                          <Box mt={0.5}>
                            <Typography variant="caption" color="textSecondary">
                              Matched: {api.matchedEntities.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        {api.endpoints && api.endpoints.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                              Endpoints:
                            </Typography>
                            {api.endpoints.map((endpoint: any, endpointIdx: number) => (
                              <Box key={endpointIdx} ml={1} mt={0.5}>
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontFamily: 'monospace',
                                    backgroundColor: '#f5f5f5',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    display: 'inline-block'
                                  }}
                                >
                                  <span style={{ color: endpoint.method === 'GET' ? '#2196f3' : endpoint.method === 'POST' ? '#4caf50' : endpoint.method === 'PUT' ? '#ff9800' : endpoint.method === 'DELETE' ? '#f44336' : '#757575', fontWeight: 'bold' }}>
                                    {endpoint.method}
                                  </span>
                                  {' '}
                                  {endpoint.path}
                                </Typography>
                                {endpoint.summary && (
                                  <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
                                    - {endpoint.summary}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {idx < toolResult.results.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      );
    }

    if (toolName === 'list_all_apis') {
      return (
        <Box>
          {toolResult.domains?.map((domain: any) => (
            <Box key={domain.domain} mb={2}>
              <Typography variant="h6" gutterBottom>
                {domain.domain.toUpperCase()} ({domain.apisCount} APIs)
              </Typography>
              <List dense>
                {domain.apis.map((api: any, idx: number) => {
                  const catalogUrl = `/catalog/default/api/${api.name}`;
                  return (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={
                          <Link href={catalogUrl} color="primary" style={{ textDecoration: 'none' }}>
                            {api.title || api.name}
                          </Link>
                        }
                        secondary={api.description}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      );
    }

    if (toolName === 'get_api_details') {
      const catalogUrl = `/catalog/default/api/${toolResult.name}`;

      return (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="body2" paragraph style={{ flex: 1, marginBottom: 0 }}>
              <strong>Description:</strong> {toolResult.description}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              href={catalogUrl}
              style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}
            >
              View in Catalog
            </Button>
          </Box>
          <Box display="flex" mb={1} flexWrap="wrap" style={{ gap: '8px' }}>
            {toolResult.system && <Chip size="small" label={`System: ${toolResult.system}`} />}
            {toolResult.type && <Chip size="small" label={`Type: ${toolResult.type}`} />}
            {toolResult.lifecycle && <Chip size="small" label={toolResult.lifecycle} />}
            {toolResult.owner && <Chip size="small" label={`Owner: ${toolResult.owner}`} variant="outlined" />}
          </Box>
          {toolResult.tags && toolResult.tags.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: '6px' }}>
                {toolResult.tags.map((tag: string, idx: number) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          {toolResult.endpoints && toolResult.endpoints.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Endpoints ({toolResult.endpointCount || toolResult.endpoints.length})
              </Typography>
              <Box>
                {toolResult.endpoints.map((endpoint: any, idx: number) => (
                  <Box
                    key={idx}
                    mb={1.5}
                    p={1.5}
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      borderLeft: `4px solid ${
                        endpoint.method === 'GET' ? '#2196f3' :
                        endpoint.method === 'POST' ? '#4caf50' :
                        endpoint.method === 'PUT' ? '#ff9800' :
                        endpoint.method === 'DELETE' ? '#f44336' :
                        endpoint.method === 'PATCH' ? '#9c27b0' :
                        '#757575'
                      }`
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Chip
                        label={endpoint.method}
                        size="small"
                        style={{
                          backgroundColor: endpoint.method === 'GET' ? '#2196f3' :
                            endpoint.method === 'POST' ? '#4caf50' :
                            endpoint.method === 'PUT' ? '#ff9800' :
                            endpoint.method === 'DELETE' ? '#f44336' :
                            endpoint.method === 'PATCH' ? '#9c27b0' :
                            '#757575',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '60px',
                          marginRight: '8px'
                        }}
                      />
                      <Typography
                        variant="body2"
                        style={{
                          fontFamily: 'monospace',
                          fontWeight: 500
                        }}
                      >
                        {endpoint.path}
                      </Typography>
                    </Box>
                    {endpoint.summary && (
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginLeft: 72 }}>
                        {endpoint.summary}
                      </Typography>
                    )}
                    {endpoint.parameters && endpoint.parameters.length > 0 && (
                      <Box ml={9} mt={0.5}>
                        <Typography variant="caption" color="textSecondary">
                          Parameters: {endpoint.parameters.join(', ')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return <pre className={classes.codeBlock}>{JSON.stringify(toolResult, null, 2)}</pre>;
  };

  return (
    <Page themeId="tool">
      <Header
        title="API Agent"
        subtitle="Ask questions about CausewayOne APIs using natural language"
      />
      <Content>
        <Box maxWidth="1200px" margin="0 auto">
          {/* Available Tools Info */}
          <InfoCard title="Available Capabilities" className={classes.toolsPanel}>
            <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
              {availableTools.map(tool => (
                <Chip
                  key={tool.name}
                  icon={<InfoIcon />}
                  label={tool.name.replace(/_/g, ' ')}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </InfoCard>

          {/* Chat Interface */}
          <Paper elevation={2}>
            <Box p={3} className={classes.chatContainer}>
              {/* Messages */}
              <div className={classes.messagesContainer}>
                {messages.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <SmartToyIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Ask me anything about CausewayOne APIs
                    </Typography>
                    <Box className={classes.suggestions}>
                      {EXAMPLE_QUERIES.map(query => (
                        <Chip
                          key={query}
                          label={query}
                          onClick={() => setInput(query)}
                          clickable
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`${classes.message} ${
                      msg.role === 'user' ? classes.userMessage : classes.assistantMessage
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <SmartToyIcon className={classes.messageIcon} color="action" />
                    )}
                    <div
                      className={`${classes.messageContent} ${
                        msg.role === 'user'
                          ? classes.userMessageContent
                          : classes.assistantMessageContent
                      }`}
                    >
                      <Typography variant="body1">{msg.content}</Typography>
                      {msg.toolResults && (
                        <div className={classes.apiResult}>
                          {renderToolResults(msg.toolResults)}
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <PersonIcon className={classes.messageIcon} color="action" />
                    )}
                  </div>
                ))}

                {loading && (
                  <Box display="flex" alignItems="center" p={2} style={{ gap: '8px' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Searching APIs...
                    </Typography>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className={classes.inputContainer}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  variant="outlined"
                  placeholder="Ask about APIs... (e.g., 'Find APIs related to projects')"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={loading}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  endIcon={<SendIcon />}
                >
                  Send
                </Button>
              </div>
            </Box>
          </Paper>
        </Box>
      </Content>
    </Page>
  );
};
